import { getCountryFlag } from '@/pages/home/_config/countries';
import {
  getDetailGamePathCommon,
  getCompanyDetailsPath,
} from '@/pages/intelligence/utils/topchartsUtils';
import { TGranularityKey } from '@intelligence/_config/enumeration/granularity';
import { ITableFormatterProp, ITableFormatterWhenOperaProp, IBasicLineResult, TDimension, TEntityType, IFetchGenreEnum, IOperationLineResult, IFetchGamesAndCompaniesMetricTrendsPrarams, IFetchGamesAndCompaniesMetricListPrarams } from './type';
import moment from 'moment';

export const METRIC_KEY_MAP: any = {
  marketType: 'entity_type',
  dimension: 'data_tag',
  rangeTime: {
    startDate: 'start_date',
    endDate: 'end_date',
    granularity: 'granularity',
  },
  sortOrder: 'order',
  sortKey: 'order_by',
  pageIndex: '',
  pageSize: 'page_size',
  selectedList: 'select_code',
  mutipleGenre: 'genre',
  singleGenre: 'genres',
  dataSource: 'sources',
};

export const WEEK_START_DAY_MAP = {
  sun: 7,
  mon: 6,
};

export const PAGE_LOG_CONFIG_KEYS: any = {
  ['mobile_operation']: 'marketProfileStatsMobileOperationData',
  ['mobile_games']: 'marketProfileStatsMobileByGames',
  ['mobile_genres']: 'marketProfileStatsMobileByGenres',
  ['mobile_companies']: 'marketProfileStatsMobileByCompanies',
};

export const GRANULARITY_MAP = {
  daily: 'dau',
  weekly: 'wau',
  monthly: 'mau',
};

// 获取品类中文
const getGenreLabelCn = (key: string, genreMap: Record<string, IFetchGenreEnum> = {}) => {
  if (!key || !genreMap) return key;
  const normalizeGenreKey = (genreKey: string) => genreKey?.trim()?.toLowerCase();
  const resultArr = key?.split('|')?.map((k) => {
    const normalizedKey = normalizeGenreKey(k);
    const matchedGenre =
      genreMap?.[normalizedKey] ||
      Object.values(genreMap).find(
        (genreItem) => normalizeGenreKey(genreItem?.genre) === normalizedKey,
      );
    return matchedGenre?.genre_cn || k;
  });
  return resultArr?.join('|');
};

export const MARKET_DIMENSION_CONFIG: any = {
  common: {
    keyMap: {
      id: 'id',
      rank: 'rank',
      // 颜色排名字段，表格第一列checkbox的颜色
      colorRank: 'colorRank',
      gametitle: 'name',
      gametitleCn: 'name_cn',
      metricList: 'metricList',
      metricListLine: 'metric_value_trends',
      abbr: 'publisher_addr',
      cover: 'cover',
      publisher: 'publisher',
      countryIcon: 'country_img',
      platform: 'platform',
      imgType: 'imgType',
      textBubble: 'big_riser_tips',
      // 右键跳转用
      linkUrl: 'linkUrl',
      metricTitle: 'title',
      metricSource: 'source',
      metricName: 'metric',
      metricRatio: 'growth_ratio',
      metricHistory: 'history',
      metricValue: 'value',
    },
    getLinkUrl: (dimen: TDimension, entityType: TEntityType, params: any) => {
      const { id, nameCn } = params;
      switch (dimen) {
        case 'games':
          return getDetailGamePathCommon({
            entityType,
            gameId: id,
            tab: 'overview',
          });
        case 'genres': {
          return `/intelligence/genreProfile/${entityType}Games/detail/insights?entityType=${entityType}&genre=${id}&genreCn=${nameCn}&genreType=main&source=iegg`;
        }
        case 'companies':
          return getCompanyDetailsPath({
            companyId: id,
            tab: 'Summary',
          });
        default:
          return '';
      }
    },
    // 根据不同粒度获取日期范围，用于折线图补齐
    getDateRange: (
      dates: string[],
      granularity: TGranularityKey,
      startDate: string,
      endDate: string,
      weekStartDay: 'sun' | 'mon',
    ) => {
      const sortedDates = [...dates].sort();
      const minDate = startDate ? new Date(startDate) : new Date(sortedDates[0]);
      const maxDate = endDate ? new Date(endDate) : new Date(sortedDates[sortedDates.length - 1]);

      // 根据粒度调整起始日期
      const adjustStartDate = (date: Date) => {
        const d = new Date(date);
        switch (granularity) {
          case 'weekly':
            d.setDate(d.getDate() - ((d.getDay() + WEEK_START_DAY_MAP[weekStartDay]) % 7)); // 调整为周第一天为周一或周日
            break;
          case 'monthly':
            d.setDate(1);
            break;
        }
        return d;
      };

      // 生成日期序列
      const start = adjustStartDate(minDate);
      const end = new Date(maxDate);
      const result = [];

      while (start <= end) {
        result.push(new Date(start));
        switch (granularity) {
          case 'daily':
            start.setDate(start.getDate() + 1);
            break;
          case 'weekly':
            start.setDate(start.getDate() + 7);
            break;
          case 'monthly':
            start.setMonth(start.getMonth() + 1);
            break;
        }
      }

      return result.map((d) => {
        switch (granularity) {
          case 'monthly':
            return `${d.toISOString().slice(0, 7)}-01`;
          default:
            return d.toISOString().split('T')[0];
        }
      });
    },
    getCompletingData: (
      listItem: IBasicLineResult,
      granularity: TGranularityKey,
      startDate: string,
      endDate: string,
      weekStartDay: 'sun' | 'mon',
    ) => {
      const allDates = listItem.history.map((h) => h.time);
      const dateRange = _that.common.getDateRange(
        allDates,
        granularity,
        startDate,
        endDate,
        weekStartDay,
      );
      const dateMap = listItem.history.reduce((acc: any, cur) => {
        acc[cur.time] = cur.value;
        return acc;
      }, {});
      return dateRange.map((date: string) => ({
        time: moment(date).format(granularity === 'monthly' ? 'YYYY-MM' : 'YYYY-MM-DD'),
        value: dateMap[date] !== undefined ? dateMap[date] : null,
      }));
    },
    tableDataFormatter: ({
      list,
      metricMap,
      keyMap,
      entityType,
      dimen,
      granularity,
      genreMap,
      ieggGenreMap,
    }: ITableFormatterProp) => ({
      result: list?.map((item) => {
        const rank = item?.[keyMap.rank];
        const abbr = item[keyMap.abbr];
        const result = {
          ...item,
          rank: rank || 0,
          colorRank: rank - 1 || 0,
          country_img: getCountryFlag(abbr),
          imgType: entityType,
          metricList: item.metric_value_list?.map((metricItem: any) => {
            const metric = metricItem?.[keyMap.metricName];
            return {
              ...metricItem,
              title:
                metricMap?.find((mapItem: any) => mapItem.metric === metricItem.metric)?.[
                  keyMap.metricTitle
                ] || '',
              metric: `${metricItem?.[keyMap.metricSource]}_${
                metric === 'dau' ? GRANULARITY_MAP[granularity] : metric
              }`,
              // growth_ratio是后端返回的环比值，也可以自己计算，取value_ratio。这里的起名比较奇怪，应该是反了
              growth_ratio: metricItem?.growth_ratio || null,
            };
          }),
          linkUrl: _that.common.getLinkUrl(dimen, entityType, {
            id: item[keyMap.id],
          }),
        };
        if (genreMap && (item.genre || item.sub_genre)) {
          const { main, sub } = genreMap;
          Object.assign(result, {
            genre_cn: getGenreLabelCn(item.genre, main),
            sub_genre_cn: getGenreLabelCn(item.sub_genre, sub),
          });
        }

        const ieggMainGenre = item?.iegg_genre || '';
        const ieggSubGenre = item?.iegg_sub_genre || '';
        const targetIeggGenreMap = ieggGenreMap || genreMap;
        if (targetIeggGenreMap && (ieggMainGenre || ieggSubGenre)) {
          const { main, sub } = targetIeggGenreMap;
          Object.assign(result, {
            iegg_genre_cn: getGenreLabelCn(ieggMainGenre, main),
            iegg_sub_genre_cn: getGenreLabelCn(ieggSubGenre, sub),
          });
        }

        return result;
      }),
      count: 0,
    }),
    tableDataFormatterWhenOpera: ({
      list,
      metricMap,
      keyMap,
    }: ITableFormatterProp) => {
      const resultMap: Record<string, any> = {};
      const resultCount: Record<string, number> = {};
      // 遍历原始数据构建分组
      list.forEach((item) => {
        const key = `${item.date}_${item.market}_${item.source}`;
        const source = item[keyMap.metricSource];
        const metric = item[keyMap.metricName];
        const value = item?.[keyMap.metricValue];
        const lastValue = item.value_ratio;
        const trueMetric = `${source}_${metric}`;
        if (!resultMap[key]) {
          resultMap[key] = {
            ...item,
            [keyMap.gametitle]: item?.[keyMap.gametitle],
            metricList: [],
          };
        }
        if (!resultCount[metric]) {
          resultCount[metric] = 0;
        }
        resultCount[metric] = resultCount[metric] + value;
        // 添加metric数据
        resultMap[key].metricList.push({
          title:
            metricMap?.find((mapItem: any) => mapItem.metric === metric)?.[keyMap.metricTitle] ||
            '',
          source,
          metric: trueMetric,
          value,
          growth_ratio: (value - lastValue) / lastValue,
        });
      });
      try {
        // 转换为数组并排序
        const result = Object.values(resultMap).sort((a, b) => Number(new Date(b.date)) - Number(new Date(a.date)));
        return {
          result,
          count: resultCount,
        };
      } catch (error) {
        console.error(error);
      }
    },
    tableDataFormatterWhenGenres: ({
      list,
      metricMap,
      keyMap,
      entityType,
      dimen,
      genreMap,
    }: ITableFormatterProp) => {
      const resultMap: Record<string, any> = {};
      const resultCount: Record<string, number> = {};
      // 遍历原始数据构建分组
      list.forEach((item) => {
        const name = item?.[keyMap.gametitle];
        const source = item[keyMap.metricSource];
        const metric = item[keyMap.metricName];
        const trueMetric = `${source}_${metric}`;
        const value = item?.[keyMap.metricValue];
        if (name === 'All') return;
        if (!resultMap[name]) {
          resultMap[name] = {
            genre: name,
            metricList: [],
          };
        }
        if (!resultCount[trueMetric]) {
          resultCount[trueMetric] = 0;
        }
        // 添加metric数据
        resultMap[name].metricList.push({
          title:
            metricMap?.find((mapItem: any) => mapItem.metric === trueMetric)?.[
              keyMap.metricTitle
            ] || '',
          source,
          metric: trueMetric,
          value,
        });
        resultCount[trueMetric] = resultCount[trueMetric] + value;
      });
      const result = Object.values(resultMap).map((item, index) => {
        const genreCn = getGenreLabelCn(item?.genre, genreMap?.main);
        // 补齐指标中未返回的项
        const metricList = metricMap.map((metriConfigItem: any) => {
          // targetQueryMetric表明从接口返回的metric中获取指标数据，当前业务中是用于progress
          const find = item[keyMap.metricList].find(
            (queryItem: IFetchGamesAndCompaniesMetricListPrarams) =>
              queryItem?.metric === (metriConfigItem?.targetQueryMetric || metriConfigItem?.metric),
          );
          let value = find?.value;
          // 是progress要注意为空的情况和被除数为0的情况
          if (find && metriConfigItem.metric.includes('progress')) {
            value =
              (find?.value || 0) / (resultCount[metriConfigItem?.targetQueryMetric] || 0) || 0;
            // progress找不到，也就是指标为0未返回的情况
          } else if (!find && metriConfigItem.metric.includes('progress')) {
            value = 0;
          }
          // 普通指标列的value不补0
          return {
            metric: metriConfigItem.metric,
            source: metriConfigItem.source,
            title: metriConfigItem.title,
            value,
          };
        });
        return {
          ...item,
          id: item[keyMap.gametitle],
          rank: index,
          colorRank: index - 1 || 0,
          genreCn,
          [keyMap.metricList]: metricList,
          linkUrl: _that.common.getLinkUrl(dimen, entityType, {
            id: item[keyMap.gametitle],
            nameCn: genreCn,
          }),
        };
      });
      return {
        result,
        count: resultCount,
      };
    },
    lineChartDataFormatter: ({
      list = [],
      keyMap,
      isCn,
      timeType,
      startDate,
      endDate,
    }: ITableFormatterWhenOperaProp) => {
      const result: any = {};
      list.forEach((item) => {
        const id = item?.[keyMap.id];
        const nameEn = item?.[keyMap.gametitle];
        const nameCn = item?.[keyMap.gametitleCn] || item?.[keyMap.gametitle];
        const name = isCn ? nameCn : nameEn;
        // const cover = item?.[keyMap.cover];
        const metricList = item?.[keyMap.metricListLine];
        metricList.forEach((metricItem: IFetchGamesAndCompaniesMetricTrendsPrarams) => {
          const { metric, date, value, source } = metricItem;
          if (!result[metric]) {
            result[metric] = [];
          }
          let entry = result[metric].find((e: IBasicLineResult) => e.name === name);
          if (!entry) {
            entry = {
              id,
              metric,
              source,
              name,
              nameEn,
              nameCn,
              // cover,
              history: [],
            };
            result[metric].push(entry);
          }
          entry.history.push({
            time: date,
            value: value === null ? null : Number(value),
          });
        });
      });

      // 补齐日期数据
      Object.keys(result).forEach((metric) => {
        result[metric].forEach((entry: IBasicLineResult) => {
          entry.history = _that.common.getCompletingData(
            entry,
            timeType,
            startDate,
            endDate,
            'sun',
          );
        });
      });
      return result;
    },
    lineChartDataFormatterWhenOpera: ({
      list = [],
      keyMap,
      metricMap,
      localeFn,
      timeType,
      startDate,
      endDate,
    }: ITableFormatterWhenOperaProp): {
      revenue: IOperationLineResult[];
    } => {
      const revenueList = list.filter((item) => item.metric === 'revenue');
      const downloadList = list.filter((item) => item.metric === 'download');
      const tmpArr = metricMap[0].children?.map((metricItem: any, index: number) => {
        const { label, key } = metricItem;
        const trueList = key === 'revenue' ? revenueList : downloadList;
        return {
          id: `${key}_${index}`,
          name: localeFn({ id: label }),
          nameKey: label,
          history: trueList.map((item) => ({
            time: item?.[keyMap.gametitle],
            value: item?.[keyMap.metricValue],
          })),
        };
      });
      // 补齐日期数据
      tmpArr.forEach((metric: IOperationLineResult) => {
        // 无点位数据不补齐
        if (metric.history?.length !== 0) {
          metric.history = _that.common.getCompletingData(
            metric,
            timeType,
            startDate,
            endDate,
            'sun',
          );
        }
      });
      return {
        revenue: tmpArr,
      };
    },
    lineChartDataFormatterWhenGenre: ({
      list = [],
      keyMap,
      isCn,
      timeType,
      genreMap,
      startDate,
      endDate,
    }: ITableFormatterWhenOperaProp): IBasicLineResult[] => {
      const result: any = {};
      list.forEach((metricItem, index) => {
        const nameEn = metricItem?.[keyMap.gametitle];
        const nameCn = getGenreLabelCn(nameEn, genreMap?.main || {});
        const name = isCn ? nameCn : nameEn;
        const id = `${name}_${index}`;
        const source = metricItem?.[keyMap.metricSource];
        const value = metricItem?.[keyMap.metricValue];
        const metric = metricItem?.[keyMap.metricName];
        if (name === 'All') return;
        if (!result[metric]) {
          result[metric] = [];
        }
        let entry = result[metric].find((e) => e.name === name);
        if (!entry) {
          entry = {
            id,
            metric,
            source,
            name,
            nameCn,
            nameEn,
            // cover,
            history: [],
          };
          result[metric].push(entry);
        }
        entry.history.push({
          time: metricItem.date,
          value: value === null ? null : Number(value),
        });
      });

      // 补齐日期数据
      Object.keys(result).forEach((metric) => {
        result[metric].forEach((entry: IBasicLineResult) => {
          entry.history = _that.common.getCompletingData(
            entry,
            timeType,
            startDate,
            endDate,
            'sun',
          );
        });
      });
      return result;
    },
  },
  mobile: {
    title: {
      operation: 'dbi_genre_profile_trend',
      games: 'dbi_genre_profile_top_games',
      genres: 'dbi_market_top_genre',
      companies: 'dbi_genre_profile_top_companies',
    },
    // 用于展示折线图顶部的segment切换，还有多折线图的key
    lineTotalText: {
      operation: [
        {
          label: 'dbi_company_metrics_sum_revenue',
          key: 'sum_revenue',
        },
        {
          label: 'dbi_calc_metric_sum_downloads',
          key: 'sum_download',
        },
      ],
    },
    lineMetricList: {
      operation: [
        {
          label: 'Revenue($)',
          key: 'revenue',
          children: [
            {
              label: 'dbi_revenue_$',
              labelCn: '收入（美元）',
              key: 'revenue',
            },
            {
              label: 'dbi_downloads',
              labelCn: '下载',
              key: 'download',
            },
          ],
        },
      ],
      games: [
        {
          label: 'dbi_revenue_$',
          key: 'revenue',
        },
        {
          label: 'dbi_downloads',
          key: 'download',
        },
        {
          label: 'dbi_mau',
          key: 'dau',
          // 隐藏的条件,多个条件以;分割
          hideCond: 'dau,wau',
        },
        {
          label: 'dbi_dau',
          key: 'dau',
          // 隐藏的条件,多个条件以;分割
          hideCond: 'mau,wau',
        },
        {
          label: 'dbi_wau',
          key: 'dau',
          // 隐藏的条件,多个条件以;分割
          hideCond: 'mau,dau',
        },
      ],
      genres: [
        {
          label: 'dbi_revenue_$',
          key: 'revenue',
        },
        {
          label: 'dbi_downloads',
          key: 'download',
        },
      ],
      companies: [
        {
          label: 'dbi_revenue_$',
          key: 'revenue',
        },
        {
          label: 'dbi_downloads',
          key: 'download',
        },
      ],
    },
    tableMetricList: {
      operation: [
        {
          title: 'dbi_total_rev_$',
          source: 'appannie',
          metric: 'appannie_revenue',
          align: 'center',
          childWidth: 240,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_downloads',
          source: 'appannie',
          metric: 'appannie_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_total_rev_$',
          source: 'sensortower',
          metric: 'sensortower_revenue',
          align: 'center',
          childWidth: 240,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
        {
          title: 'dbi_downloads',
          source: 'sensortower',
          metric: 'sensortower_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
      ],
      games: [
        {
          title: 'dbi_revenue_$',
          source: 'appannie',
          metric: 'appannie_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_revenue_$',
          source: 'sensortower',
          metric: 'sensortower_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
        {
          title: 'dbi_downloads',
          source: 'appannie',
          metric: 'appannie_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_downloads',
          source: 'sensortower',
          metric: 'sensortower_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
        {
          title: 'dbi_mau',
          source: 'appannie',
          metric: 'appannie_mau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          // 隐藏的条件,多个条件以;分割
          hideCond: 'sensortower;dau,wau',
        },
        {
          title: 'dbi_dau',
          source: 'appannie',
          metric: 'appannie_dau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          // 隐藏的条件,多个条件以;分割
          hideCond: 'sensortower;mau,wau',
        },
        {
          title: 'dbi_wau',
          source: 'appannie',
          metric: 'appannie_dau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          // 隐藏的条件,多个条件以;分割
          hideCond: 'sensortower;mau,dau',
        },
        {
          title: 'dbi_mau',
          source: 'sensortower',
          metric: 'sensortower_mau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          // 隐藏的条件,多个条件以;分割
          hideCond: 'appannie;dau,wau',
        },
        {
          title: 'dbi_dau',
          source: 'appannie',
          metric: 'appannie_dau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          // 隐藏的条件,多个条件以;分割
          hideCond: 'appannie;mau,wau',
        },
        {
          title: 'dbi_wau',
          source: 'appannie',
          metric: 'appannie_wau',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie;mau,dau',
        },
      ],
      genres: [
        {
          title: 'dbi_revenue_$',
          source: 'appannie',
          metric: 'appannie_revenue',
          align: 'center',
          childWidth: 180,
          childAlign: 'right',
          isNeedSort: true,
          noRatio: true,
          hideCond: 'sensortower',
          type: 'noGrowth',
        },
        {
          title: 'dbi_revenue_$',
          source: 'sensortower',
          metric: 'sensortower_revenue',
          align: 'center',
          childWidth: 180,
          childAlign: 'right',
          isNeedSort: true,
          noRatio: true,
          hideCond: 'appannie',
          type: 'noGrowth',
        },
        {
          title: 'dbi_market_percent_revenue',
          metric: 'appannie_revenue_progress',
          targetQueryMetric: 'appannie_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: false,
          isNoTips: true,
          hideCond: 'sensortower',
          type: 'percent',
        },
        {
          title: 'dbi_market_percent_revenue',
          metric: 'sensortower_revenue_progress',
          targetQueryMetric: 'sensortower_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: false,
          isNoTips: true,
          type: 'percent',
          hideCond: 'appannie',
        },
        {
          title: 'dbi_downloads',
          source: 'appannie',
          metric: 'appannie_download',
          align: 'center',
          childWidth: 180,
          childAlign: 'right',
          isNeedSort: true,
          noRatio: true,
          hideCond: 'sensortower',
          type: 'noGrowth',
        },
        {
          title: 'dbi_downloads',
          source: 'sensortower',
          metric: 'sensortower_download',
          align: 'center',
          childWidth: 180,
          childAlign: 'right',
          isNeedSort: true,
          noRatio: true,
          hideCond: 'appannie',
          type: 'noGrowth',
        },
        {
          title: 'dbi_market_percent_downloads',
          metric: 'appannie_download_progress',
          targetQueryMetric: 'appannie_download',
          align: 'center',
          childWidth: 210,
          childAlign: 'right',
          isNeedSort: false,
          isNoTips: true,
          hideCond: 'sensortower',
          type: 'percent',
        },
        {
          title: 'dbi_market_percent_downloads',
          metric: 'sensortower_download_progress',
          targetQueryMetric: 'sensortower_download',
          align: 'center',
          childWidth: 210,
          childAlign: 'right',
          isNeedSort: false,
          isNoTips: true,
          type: 'percent',
          hideCond: 'appannie',
        },
      ],
      companies: [
        {
          title: 'dbi_revenue_$',
          source: 'appannie',
          metric: 'appannie_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_downloads',
          source: 'appannie',
          metric: 'appannie_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'sensortower',
        },
        {
          title: 'dbi_revenue_$',
          source: 'sensortower',
          metric: 'sensortower_revenue',
          align: 'center',
          childWidth: 200,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
        {
          title: 'dbi_downloads',
          source: 'sensortower',
          metric: 'sensortower_download',
          align: 'center',
          childWidth: 190,
          childAlign: 'right',
          isNeedSort: true,
          hideCond: 'appannie',
        },
      ],
    },
    firstCol: {
      operation: [
        {
          title: 'dbi_date',
          type: 'date',
          key: 'gametitle',
          hasSort: true,
          defaultSort: true,
          hasCheckbox: false,
          hasRank: false,
          // 排名是否自增
          isAutoIncrementingRank: false,
          // 排名字段，默认为id
          rankField: 'rank',
          colorRankField: 'colorRank',
          canBeClick: false,
        },
      ],
      games: [
        {
          title: 'dbi_games',
          type: 'gameInfo',
          key: 'gametitle',
          hasSort: false,
          defaultSort: false,
          hasCheckbox: true,
          hasRank: true,
          isAutoIncrementingRank: false,
          rankField: 'rank',
          colorRankField: 'colorRank',
          canBeClick: true,
          width: 360,
        },
      ],
      genres: [
        {
          title: 'dbi_label_genre',
          type: 'text',
          key: 'gametitle',
          hasSort: false,
          defaultSort: false,
          hasCheckbox: true,
          hasRank: false,
          isAutoIncrementingRank: true,
          rankField: 'rank',
          colorRankField: 'colorRank',
          canBeClick: false,
        },
      ],
      companies: [
        {
          title: 'dbi_company_companies',
          type: 'company',
          key: 'gametitle',
          hasSort: false,
          defaultSort: false,
          hasCheckbox: true,
          hasRank: true,
          rankField: 'rank',
          colorRankField: 'colorRank',
          isAutoIncrementingRank: false,
          canBeClick: true,
        },
      ],
    },
    specialCol: {
      operation: null,
      games: [
        {
          type: 'text',
          title: 'dbi_main_genre',
          key: 'iegg_genre',
          // key: 'genre',
          width: 180,
          align: 'center',
          childWidth: 140,
          childAlign: 'left',
        },
        {
          type: 'text',
          title: 'dbi_sub_genre',
          key: 'iegg_sub_genre',
          // key: 'sub_genre',
          align: 'center',
          childWidth: 220,
          childAlign: 'left',
        },
        {
          type: 'action',
          title: 'dbi_actions',
          key: 'dbi_details',
          linkUrl: '',
          width: 140,
          align: 'center',
          childWidth: 100,
          childAlign: 'left',
        },
      ],
      genres: [],
      companies: [
        {
          type: 'action',
          title: 'dbi_actions',
          key: 'dbi_details',
          linkUrl: '',
          align: 'center',
          childWidth: 100,
          childAlign: 'left',
        },
      ],
    },
    tableFormatter: {
      operation: (params: ITableFormatterProp) => _that.common.tableDataFormatterWhenOpera(params),
      games: (params: ITableFormatterProp) => _that.common.tableDataFormatter(params),
      genres: (params: ITableFormatterProp) => _that.common.tableDataFormatterWhenGenres(params),
      companies: (params: ITableFormatterProp) => _that.common.tableDataFormatter(params),
    },
    lineFormatter: {
      operation: (params: ITableFormatterWhenOperaProp) =>
        _that.common.lineChartDataFormatterWhenOpera(params),
      games: (params: ITableFormatterWhenOperaProp) => _that.common.lineChartDataFormatter(params),
      genres: (params: ITableFormatterWhenOperaProp) =>
        _that.common.lineChartDataFormatterWhenGenre(params),
      companies: (params: ITableFormatterWhenOperaProp) =>
        _that.common.lineChartDataFormatter(params),
    },
    keyMap: {
      operation: () =>
        Object.assign({}, _that.common.keyMap, {
          id: 'date',
          gametitle: 'date',
          metricList: 'metricList',
        }),
      games: () =>
        Object.assign({}, _that.common.keyMap, {
          metricList: 'metricList',
        }),
      genres: () =>
        Object.assign({}, _that.common.keyMap, {
          id: 'genre',
          gametitle: 'genre',
          gametitleCn: 'genreCn',
          metricList: 'metricList',
        }),
      companies: () =>
        Object.assign({}, _that.common.keyMap, {
          id: 'uuid',
          metricList: 'metricList',
          abbr: 'country_abbre',
          publisher: 'location_identifiers_city',
          country: 'location_identifiers_country',
        }),
    },
  },
};

const _that = MARKET_DIMENSION_CONFIG;
